/** 地面の材質（見た目）。 */
export type GroundKind = 'grid' | 'grass' | 'sand' | 'snow' | 'asphalt' | 'sea';

/**
 * 背景（空・地面・光・霧）の見た目。保存データの環境（`@rcs/data-schema`）から、アプリ側で作って渡す。
 * 色は `#rrggbb`。
 */
export interface EnvironmentLook {
  /** 空の上の色 */
  readonly skyTop: string;
  /** 地平線のあたりの色（霧の色にもなる） */
  readonly skyHorizon: string;
  /** 霧の濃さ（0 = なし、1 = 濃い） */
  readonly fogDensity: number;
  /** 地面の材質。`grid` は格子の線を引いた確認用の地面（地面の色は使わない） */
  readonly ground: GroundKind;
  /** 地面の色 */
  readonly groundColor: string;
  /** 時刻（0〜24 時） */
  readonly timeOfDay: number;
  /** 太陽の強さ（1 = 標準） */
  readonly sunIntensity: number;
  /** 太陽の方角 [度]（0 = 北 = -Z、90 = 東 = +X、180 = 南）。省略は、時刻から決める */
  readonly sunAzimuth?: number | undefined;
  /** 太陽の高さ [度]（0 = 地平線、90 = 真上）。省略は、時刻から決める */
  readonly sunElevation?: number | undefined;
}

/** 既定の見た目（昼・格子の地面）。背景を指定しないときに使う。 */
export const DEFAULT_ENVIRONMENT_LOOK: EnvironmentLook = {
  skyTop: '#5b9ee1',
  skyHorizon: '#bfd9f2',
  fogDensity: 0,
  ground: 'grid',
  groundColor: '#b9c9d8',
  timeOfDay: 12,
  sunIntensity: 1,
};

/** 光の設定。 */
export interface LightingSetup {
  /** 太陽の向き（原点から太陽へ向かう単位ベクトル） */
  readonly sunDirection: readonly [number, number, number];
  /** 太陽の光の強さ */
  readonly sunIntensity: number;
  /** 太陽の光の色（`0xrrggbb`。低い太陽は赤みを帯びる） */
  readonly sunColor: number;
  /** 空からの環境光の強さ */
  readonly ambientIntensity: number;
}

/** 昼（12 時）の太陽の高さ [rad]。 */
const NOON_ELEVATION = 1.2;
/** 夜でもわずかに照らす（月明かり）ための、太陽の最低の高さ [rad]。 */
const MIN_ELEVATION = 0.14;
/** 昼（12 時）の太陽の向き（水平面での角度 [rad]）。 */
const NOON_AZIMUTH = Math.atan2(0.5, 0.3);

/**
 * 時刻と太陽の強さから、光の設定を求める（純粋関数）。
 * 6 時に東、12 時に真上近く、18 時に西へ動く。地平線の下（夜）は、弱い光を上から当てる。
 * 12 時・強さ 1 は、従来の固定の光と同じ。
 */
export function lightingFor(
  timeOfDay: number,
  sunIntensity: number,
  sun?: { readonly azimuth: number; readonly elevation: number },
): LightingSetup {
  if (sun) return fixedSunLighting(sunIntensity, sun.azimuth, sun.elevation);
  const t = ((timeOfDay % 24) + 24) % 24;
  const raw = Math.sin(((t - 6) / 12) * Math.PI);
  const day = Math.max(0, raw);
  const elevation = Math.max(MIN_ELEVATION, NOON_ELEVATION * day);
  const azimuth = NOON_AZIMUTH + ((t - 12) / 12) * Math.PI * 0.9;
  const horizontal = Math.cos(elevation);
  const sunDirection: [number, number, number] = [
    horizontal * Math.sin(azimuth),
    Math.sin(elevation),
    horizontal * Math.cos(azimuth),
  ];
  // 低い太陽ほど赤みを帯びる（昼は白）
  const warm = Math.min(1, Math.max(0, 1 - day * 1.6));
  const g = Math.round(255 - 60 * warm);
  const b = Math.round(255 - 130 * warm);
  return {
    sunDirection,
    sunIntensity: 1.5 * sunIntensity * (0.15 + 0.85 * Math.sqrt(day)),
    sunColor: (255 << 16) | (g << 8) | b,
    ambientIntensity: 1.2 * (0.3 + 0.7 * day),
  };
}

/** 地面の格子を除いた、地面の色の既定。 */
export const DEFAULT_GROUND_COLORS: Readonly<Record<GroundKind, string>> = {
  grid: '#b9c9d8',
  grass: '#5f9a4a',
  sand: '#d9bf86',
  snow: '#eef2f6',
  asphalt: '#4a4d52',
  sea: '#2f6fa8',
};

/**
 * 太陽の方角・高さを指定した光の設定。高さが低いほど暗く、赤みを帯びる。
 * 方角は北 = -Z・東 = +X（時計回り）。
 */
function fixedSunLighting(
  sunIntensity: number,
  azimuthDeg: number,
  elevationDeg: number,
): LightingSetup {
  const azimuth = (azimuthDeg * Math.PI) / 180;
  const elevation = Math.max(0.05, (elevationDeg * Math.PI) / 180);
  const day = Math.sin(elevation);
  const horizontal = Math.cos(elevation);
  const warm = Math.min(1, Math.max(0, 1 - day * 1.6));
  return {
    sunDirection: [
      horizontal * Math.sin(azimuth),
      Math.sin(elevation),
      -horizontal * Math.cos(azimuth),
    ],
    sunIntensity: 1.5 * sunIntensity * (0.15 + 0.85 * Math.sqrt(day)),
    sunColor: (255 << 16) | (Math.round(255 - 60 * warm) << 8) | Math.round(255 - 130 * warm),
    ambientIntensity: 1.2 * (0.3 + 0.7 * day),
  };
}
